import { Immutable } from "immer";
import React, { useEffect, useMemo, useRef } from "react";
import {
    IGA4ListMetadata,
    trackSelectItemEventWithItem,
    trackViewItemListEventWithItems,
} from "tsi-common-react/src/utils/analytics";
import { slugify } from "tsi-common-react/src/utils/text";
import { t } from "ttag";

import type { FeaturedProductsBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getSpacingClasses } from "../spacing";

import styles from "./index.module.scss";

type Props = Immutable<{
    value: Value;
}>;

type ProductEntry = NonNullable<Value["products"]>[number];

/**
 * Faithful client port of the legacy server-rendered
 * ``cms/blocks/featured-products-block.html`` (stearns-specific toolbox
 * ``featured_products`` block).
 *
 * The serialized value carries the chosen variant only as a pk (``product``),
 * plus the literal ``product_name`` / ``product_details`` rich text, a literal
 * ``price`` string, and a ``product_link`` LinkBlock. Pricing is NOT computed
 * here -- the legacy template rendered ``${{ product.price }}`` straight from
 * the ``price`` CharBlock, so it stays a literal string.
 *
 * The per-product star rating is SSR'd: the block serializes each entry's
 * chosen-variant ``rating`` / ``num_reviews`` server-side (the same denormalized
 * values the legacy template read via ``selectedProduct.rating``), so the stars
 * render on the server with NO client fetch. The star markup is hand-rendered to
 * reproduce the legacy ``shared/_star-rating.html`` partial (the module's
 * ``.starRating`` rules target ``.stars`` and a bare
 * ``<p class="number caption">``; neither ``RatingGraphic``
 * nor the shared ``StarRating`` component emit those exact class names). The
 * full / half / outline SVG paths are copied from
 * ``client/src/svg/{star,star-half-outline,star-outline}.svg``.
 *
 * The per-card GA4 ``select_item`` click event is SSR'd too: the legacy ``<a>``
 * carried the click payload via ``select_item_tracking_attrs`` (an inline
 * ``onclick`` built server-side). The block now serializes the SAME
 * ``build_catalog_item`` payload per card (``entry.select_item``) and the
 * client ``<a>`` fires ``trackSelectItemEventWithItem`` on click -- no hydrated
 * product / client fetch. ``entry.select_item`` is ``null`` when no product is
 * chosen, so the handler no-ops (matching the legacy ``_build_select_item
 * _event`` returning ``None``).
 *
 * The ``view_item_list`` impression event is also SSR'd: the block serializes
 * the GA4 item payload server-side (``view_item_list_items``, built by the same
 * ``build_catalog_item`` helper the legacy ``track_view_item_list`` tag used),
 * so the impression fires once on mount from server data with NO client fetch.
 * The client supplies the list metadata (see ``buildListMetadata`` for the
 * item_list_id/item_list_name parity with the server tag).
 */

const STAR_FULL_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15L.8 21.1c-.4-.4-.8-.9-.8-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5";
const STAR_HALF_PATH =
    "m38.3 31.7 2.6 15c.2 1.3-.3 2.1-1.2 2.1-.3 0-.7-.1-1.2-.4l-13.5-7-13.5 7.1c-.5.2-.9.4-1.2.4-.9 0-1.4-.8-1.2-2.1l2.6-15L.8 21.1c-1.3-1.3-.9-2.6.9-2.8l15.1-2.2 6.8-13.7q.6-1.2 1.5-1.2c.5 0 1.1.4 1.5 1.2l6.8 13.7 15.1 2.2c1.8.3 2.2 1.5.9 2.8zm5.1-10.3-10.7-1.6-2-.3-.9-1.8L25 8.1V37l1.8.9 9.6 5.1-1.8-10.7-.4-2 1.5-1.4z";
const STAR_OUTLINE_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15-11-10.7c-.3-.4-.7-.9-.7-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5m-5.8.3-12.7-1.9L25 8.1l-5.7 11.5-12.7 1.8 9.2 8.9L13.6 43 25 37l11.3 6-2.2-12.6z";

const StarSVG = (props: { path: string }): React.JSX.Element => (
    <svg
        className="star-rating__star"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 50 50"
    >
        <path d={props.path} />
    </svg>
);

/**
 * Reproduce the legacy ``shared/_star-rating.html`` 5-star thresholds: for the
 * Nth star (1-indexed), render a full star when ``rating >= N - 0.25``, a half
 * star when ``N - 0.75 <= rating < N - 0.25``, otherwise an outline star. A null
 * rating renders all outline stars.
 */
const renderStars = (rating: number | null): React.JSX.Element[] => {
    return [1, 2, 3, 4, 5].map((n) => {
        let path = STAR_OUTLINE_PATH;
        if (rating !== null) {
            if (rating >= n - 0.25) {
                path = STAR_FULL_PATH;
            } else if (rating >= n - 0.75) {
                path = STAR_HALF_PATH;
            }
        }
        return <StarSVG key={n} path={path} />;
    });
};

const ProductCard = (
    props: Immutable<{ entry: ProductEntry; listMetadata: IGA4ListMetadata }>,
): React.JSX.Element => {
    const { entry, listMetadata } = props;

    // The per-product star rating is SSR'd: ``rating`` / ``num_reviews`` are the
    // chosen variant's server-serialized (and parent-rolled-up) review values,
    // the same ones the legacy template read via ``selectedProduct.rating``.
    const rating = entry.rating;
    const numReviews = entry.num_reviews;

    const link = entry.product_link;
    // Match the legacy ``link-block.html`` Django truthiness chain
    // (`{% if value.page %}{% elif value.external_url %}{% else %}#`): an
    // empty-string `external_url` is falsy and falls through to `#`, so use
    // `||` (not `??`, which would keep an empty string).
    const href = link?.page?.url || link?.external_url || "#";

    return (
        <div className={styles.product}>
            <RichTextBlock
                className="rich-text"
                value={entry.product_name ?? ""}
            />
            <div className={styles.starRating}>
                <div className={styles.stars}>
                    {rating !== null ? (
                        <span className="ada-screenreader-only">
                            {t`Rated ${rating} out of 5 stars`}
                        </span>
                    ) : null}
                    {renderStars(rating)}
                </div>
                {numReviews ? (
                    <p className="number caption">
                        ({new Intl.NumberFormat().format(numReviews)})
                    </p>
                ) : null}
            </div>
            <RichTextBlock
                className="rich-text"
                value={entry.product_details ?? ""}
            />
            <footer>
                <h4 className={styles.price}>${entry.price}</h4>
                <a
                    href={href}
                    className={link?.is_button ? "button" : undefined}
                    onClick={() => {
                        // Fire the per-card GA4 ``select_item`` from the
                        // server-serialized payload (no client fetch),
                        // reproducing the legacy ``select_item_tracking_attrs``
                        // onclick. No-ops when ``select_item`` is null.
                        trackSelectItemEventWithItem(
                            entry.select_item,
                            listMetadata,
                        );
                    }}
                >
                    {link?.display_name}
                </a>
            </footer>
        </div>
    );
};

// Literal GA4 list-name fallback, matching the legacy Django tag's
// ``fallback_name='Featured Products'`` (the block has no editor heading field,
// so the server always fell back to this literal).
const FALLBACK_LIST_NAME = "Featured Products";

/**
 * Reproduce the server ``_build_item_list_metadata`` output for this block.
 *
 * The block has no heading field, so the server ``_normalize_tracking_name_id``
 * falls back to ``fallback_name`` ('Featured Products') for ``item_list_name``
 * and ``slugify(fallback_name)`` -> ``featured-products`` for the id. The legacy
 * ``{% track_view_item_list %}`` call passes NO ``block_index``, and
 * ``_resolve_block_index`` returns ``None`` here (the block's StructValue has no
 * ``id``, and ``self`` in the block template IS that StructValue), so
 * ``_normalize_tracking_name_id`` does NOT append a ``-{block_index}`` suffix
 * (it only appends when ``block_index is not None``). The id is therefore
 * ``featured-products`` with no trailing hyphen -- verified against the real
 * server builder -- so both the ``view_item_list`` impression and the per-card
 * ``select_item`` clicks land in the same GA4 list the server emits.
 */
const buildListMetadata = (): IGA4ListMetadata => ({
    item_list_id: slugify(FALLBACK_LIST_NAME),
    item_list_name: FALLBACK_LIST_NAME,
});

export const FeaturedProductsBlock = (props: Props): React.JSX.Element => {
    const v = props.value;
    const entries = v.products ?? [];

    // Shared list metadata for both the ``view_item_list`` impression and the
    // per-card ``select_item`` clicks (the legacy tags built both from the same
    // ``_build_item_list_metadata``). Memoized so the impression effect fires
    // once.
    const listMetadata = useMemo(() => buildListMetadata(), []);

    const firedRef = useRef(false);

    // Fire the ``view_item_list`` impression exactly once on mount from the
    // server-serialized GA4 items (``view_item_list_items``, built by the same
    // ``build_catalog_item`` helper as the legacy ``track_view_item_list``
    // tag). No client product fetch: every on-load value is SSR'd. The
    // ``firedRef`` guard prevents a re-fire if a later render re-runs this
    // effect.
    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        firedRef.current = true;
        trackViewItemListEventWithItems(v.view_item_list_items, listMetadata);
        // Fire-on-mount only: the block value is static per page render.
    }, [v.view_item_list_items, listMetadata]);

    return (
        <section
            className={concatClassNames([
                styles.featuredProducts,
                getSpacingClasses(v.spacing),
            ])}
        >
            {entries.map((entry, i) => (
                <ProductCard
                    key={i}
                    entry={entry}
                    listMetadata={listMetadata}
                />
            ))}
        </section>
    );
};
